import { type PcfSettingsDto } from '../../../shared/api/pipeex/pcfSettingsClient';
import { getPipeexUiProfileMetadata } from '../../../shared/api/pipeex/pipeexProfileUiState';
import {
  buildAdditionalCategoryAdditionOperations,
  collectConnectorBindingParameters,
  requiresMappedProjectInstanceBinding,
} from '../../../shared/lib/hooks/useParameterAddition';
import { extractBoltParameterNames } from '../../../shared/services/boltParameterSetup';
import {
  collectMappedParameterReferencesFromSettingsDto,
  getBomProfileParameterGroups,
  normalizeLookupName,
  type MappingParameterSource,
} from './readiness/mappedParameterReferences';
import {
  getSettingsDtoAdditionalCategoriesConfiguration,
  getSettingsDtoBoltConfigurationInput,
  getSettingsDtoConnectorConfiguration,
  getSettingsDtoMenuConfiguration,
} from './settingsDtoFamilyDestination';

export type ProfileParameterKind =
  | 'projectType'
  | 'projectInstance'
  | 'project'
  | 'familyType'
  | 'familyInstance'
  | 'system'
  | 'unknown';

export interface ProfileContentsEntry {
  name: string;
  kind: ProfileParameterKind;
  sources: MappingParameterSource[];
}

export interface ProfileContentsGroup {
  kind: ProfileParameterKind;
  entries: ProfileContentsEntry[];
}

export interface ProfileContentsSummary {
  totalCount: number;
  groups: ProfileContentsGroup[];
}

/** Structural subset of the Revit parameter catalog entry used for classification. */
export interface ProfileContentsCatalogParameter {
  name: string;
  label?: string;
  parameterType?: string;
  isProjectBinding?: boolean;
}

const GROUP_ORDER: ProfileParameterKind[] = [
  'projectType',
  'projectInstance',
  'project',
  'familyType',
  'familyInstance',
  'system',
  'unknown',
];

/** Display label for one parameter group, shared by every preview surface. */
export function getProfileParameterKindLabel(
  kind: ProfileParameterKind
): string {
  switch (kind) {
    case 'projectType':
      return 'Project type parameters';
    case 'projectInstance':
      return 'Project instance parameters';
    case 'project':
      return 'Project parameters — type/instance not recorded';
    case 'familyType':
      return 'Family type parameters';
    case 'familyInstance':
      return 'Family instance parameters';
    case 'system':
      return 'System parameters';
    default:
      return 'Parameter scope not recorded in this profile';
  }
}

function getIntendedKind(
  destination: 'project' | 'family',
  bindingKind: 'type' | 'instance'
): ProfileParameterKind {
  if (destination === 'project') {
    return bindingKind === 'type' ? 'projectType' : 'projectInstance';
  }

  return bindingKind === 'type' ? 'familyType' : 'familyInstance';
}

function addIntendedKinds(
  target: Map<string, ProfileParameterKind>,
  parameterNames: Iterable<string>,
  kind: ProfileParameterKind
): void {
  Array.from(parameterNames).forEach((parameterName) => {
    const key = normalizeLookupName(parameterName);
    if (key) {
      target.set(key, kind);
    }
  });
}

/**
 * Reads scope choices from the profile itself. These choices describe where
 * parameters are intended to be created and remain valid before Revit has any
 * matching parameters to inspect.
 */
function buildPersistedParameterKinds(dto: PcfSettingsDto): {
  kinds: Map<string, ProfileParameterKind>;
  connectorBindingKind: 'type' | 'instance' | null;
} {
  const kinds = new Map<string, ProfileParameterKind>();
  const menuConfig = getSettingsDtoMenuConfiguration(dto);
  const connectorConfig = getSettingsDtoConnectorConfiguration(dto);
  const connectorBindingKind = connectorConfig?.parameterBindingKind ?? null;

  if (menuConfig && connectorConfig) {
    const connectorKind = getIntendedKind(
      connectorConfig.parameterDestination,
      connectorConfig.parameterBindingKind
    );
    addIntendedKinds(
      kinds,
      [
        ...collectConnectorBindingParameters({ menuConfig, connectorConfig }),
        connectorConfig.weightValue.value,
      ],
      connectorKind
    );
  }

  const boltConfig = getSettingsDtoBoltConfigurationInput(dto);
  if (boltConfig) {
    addIntendedKinds(
      kinds,
      extractBoltParameterNames(boltConfig),
      getIntendedKind(
        boltConfig.parameterDestination === 'family' ? 'family' : 'project',
        boltConfig.parameterBindingKind === 'instance' ? 'instance' : 'type'
      )
    );
  }

  const additionalConfig = getSettingsDtoAdditionalCategoriesConfiguration(dto);
  if (additionalConfig) {
    buildAdditionalCategoryAdditionOperations(additionalConfig).forEach((operation) => {
      addIntendedKinds(
        kinds,
        operation.parameterNames,
        getIntendedKind(operation.destination, operation.bindingKind)
      );
    });
  }

  if (connectorBindingKind) {
    const setupParameters = getPipeexUiProfileMetadata(dto.advancedSettings)
      ?.wizard?.setup.parameters ?? [];
    setupParameters
      .filter((parameter) => parameter.selected)
      .forEach((parameter) => {
        const key = normalizeLookupName(parameter.name);
        if (key && !kinds.has(key)) {
          kinds.set(key, getIntendedKind('project', connectorBindingKind));
        }
      });
  }

  return { kinds, connectorBindingKind };
}

function getBomEntryName(entry: unknown): string {
  if (!entry || typeof entry !== 'object') {
    return '';
  }

  const record = entry as { RevitParameter?: unknown; revitParameter?: unknown };
  const value = record.RevitParameter ?? record.revitParameter;
  return typeof value === 'string' ? value : '';
}

function classifyFromCatalog(
  parameter: ProfileContentsCatalogParameter | undefined
): ProfileParameterKind {
  if (!parameter) {
    return 'unknown';
  }

  if (parameter.isProjectBinding === true) {
    return classifyProjectFromCatalog(parameter);
  }

  if (parameter.parameterType === 'Type') {
    return 'familyType';
  }

  if (parameter.parameterType === 'Instance') {
    return 'familyInstance';
  }

  return 'unknown';
}

function classifyProjectFromCatalog(
  parameter: ProfileContentsCatalogParameter | undefined
): ProfileParameterKind {
  if (parameter?.parameterType === 'Type') {
    return 'projectType';
  }

  if (parameter?.parameterType === 'Instance') {
    return 'projectInstance';
  }

  return 'project';
}

function classifyProfileParameter(input: {
  name: string;
  lookupName: string;
  bomProjectNames: ReadonlySet<string>;
  bomSystemNames: ReadonlySet<string>;
  persistedKinds: ReadonlyMap<string, ProfileParameterKind>;
  connectorBindingKind: 'type' | 'instance' | null;
  catalogParameter?: ProfileContentsCatalogParameter;
}): ProfileParameterKind {
  if (input.bomSystemNames.has(input.lookupName)) {
    return 'system';
  }

  if (requiresMappedProjectInstanceBinding(input.name)) {
    return 'projectInstance';
  }

  const persistedKind = input.persistedKinds.get(input.lookupName);
  if (persistedKind) {
    return persistedKind;
  }

  if (input.bomProjectNames.has(input.lookupName)) {
    return input.connectorBindingKind
      ? getIntendedKind('project', input.connectorBindingKind)
      : classifyProjectFromCatalog(input.catalogParameter);
  }

  return classifyFromCatalog(input.catalogParameter);
}

/**
 * Summarizes which parameters a saved profile references and how each one is
 * bound, so the profile picker can preview large profiles before loading.
 * Saved wizard scope is authoritative even when the parameters have not been
 * added yet. The live catalog is only a fallback for legacy/unscoped entries.
 */
export function buildProfileContents(
  dto: PcfSettingsDto,
  catalog: ReadonlyArray<ProfileContentsCatalogParameter>
): ProfileContentsSummary {
  const references = collectMappedParameterReferencesFromSettingsDto(dto);
  const bomGroups = getBomProfileParameterGroups(dto.bomParameters);
  const bomProjectNames = new Set(
    bomGroups.projectParameters.map((entry) => normalizeLookupName(getBomEntryName(entry)))
  );
  const bomSystemNames = new Set(
    bomGroups.systemParameters.map((entry) => normalizeLookupName(getBomEntryName(entry)))
  );
  bomProjectNames.delete('');
  bomSystemNames.delete('');
  const persisted = buildPersistedParameterKinds(dto);

  const catalogByName = new Map<string, ProfileContentsCatalogParameter>();
  catalog.forEach((parameter) => {
    const nameKey = normalizeLookupName(parameter.name);
    if (nameKey && !catalogByName.has(nameKey)) {
      catalogByName.set(nameKey, parameter);
    }

    const labelKey = normalizeLookupName(parameter.label ?? '');
    if (labelKey && !catalogByName.has(labelKey)) {
      catalogByName.set(labelKey, parameter);
    }
  });

  const entries: ProfileContentsEntry[] = references.map((reference) => {
    const lookupName = normalizeLookupName(reference.name);
    const kind = classifyProfileParameter({
      name: reference.name,
      lookupName,
      bomProjectNames,
      bomSystemNames,
      persistedKinds: persisted.kinds,
      connectorBindingKind: persisted.connectorBindingKind,
      catalogParameter: catalogByName.get(lookupName),
    });

    return { name: reference.name, kind, sources: reference.sources };
  });

  const groups = GROUP_ORDER.flatMap((kind) => {
    const groupEntries = entries.filter((entry) => entry.kind === kind);
    return groupEntries.length > 0 ? [{ kind, entries: groupEntries }] : [];
  });

  return { totalCount: entries.length, groups };
}
